import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background-color: var(--bg-primary);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  overflow: hidden;
`;

const BackgroundMatrix = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;
`;

const MatrixColumn = styled(motion.div)`
  position: absolute;
  top: -20%;
  font-family: var(--font-code);
  color: var(--accent-primary);
  opacity: 0.4;
  font-size: 12px;
  white-space: nowrap;
  text-shadow: 0 0 8px var(--accent-primary);
`;

const GlitchContainer = styled(motion.div)`
  position: relative;
  z-index: 1;
`;

const CenterContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 30px rgba(0, 200, 255, 0.2);
  position: relative;
  overflow: hidden;
  z-index: 2;
`;

const ScanLine = styled(motion.div)`
  position: absolute;
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, transparent, var(--accent-primary), transparent);
  opacity: 0.8;
`;

const GridOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: repeating-linear-gradient(
    rgba(0, 200, 255, 0.03) 0px,
    rgba(0, 200, 255, 0.03) 1px,
    transparent 1px,
    transparent 50px
  ),
  repeating-linear-gradient(
    90deg,
    rgba(0, 200, 255, 0.03) 0px,
    rgba(0, 200, 255, 0.03) 1px,
    transparent 1px,
    transparent 50px
  );
  pointer-events: none;
`;

const MultipleConsoles = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 90%;
  max-width: 600px;
  margin-top: 15px;
`;

const ConsoleWindow = styled(motion.div)`
  background-color: rgba(0, 0, 0, 0.7);
  border: 1px solid var(--accent-secondary);
  border-radius: 4px;
  padding: 10px;
  width: 100%;
  height: 120px;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(to bottom, var(--accent-secondary), transparent);
    opacity: 0.2;
  }
`;

const ConsoleHeader = styled.div`
  font-family: var(--font-mono);
  color: var(--accent-secondary);
  font-size: 10px;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
  text-transform: uppercase;
`;

const ConsoleText = styled.div`
  font-family: var(--font-code);
  color: var(--text-secondary);
  font-size: 11px;
  height: calc(100% - 20px);
  overflow: hidden;
`;

const CircularProgress = styled(motion.div)`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 20px;
  
  &:before {
    content: '';
    position: absolute;
    width: 110%;
    height: 110%;
    border-radius: 50%;
    border: 2px solid rgba(0, 200, 255, 0.1);
  }
`;

const ProgressRing = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: var(--accent-primary);
  border-left-color: var(--accent-primary);
`;

const InnerRing = styled(motion.div)`
  position: absolute;
  width: 80%;
  height: 80%;
  border-radius: 50%;
  border: 2px solid transparent;
  border-right-color: var(--accent-secondary);
  border-bottom-color: var(--accent-secondary);
`;

const HexagonContainer = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Hexagon = styled(motion.div)`
  width: 60px;
  height: 70px;
  background: linear-gradient(135deg, var(--accent-primary) 0%, transparent 100%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  opacity: 0.5;
`;

const DataPoints = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const DataPoint = styled(motion.div)`
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: var(--accent-primary);
  border-radius: 50%;
  opacity: 0.8;
`;

const ProgressText = styled(motion.div)`
  position: absolute;
  font-family: var(--font-mono);
  font-size: 18px;
  color: var(--accent-primary);
  text-shadow: 0 0 10px var(--accent-primary);
`;

const LoadingTexts = styled.div`
  display: flex;
  gap: 30px;
  margin-top: 10px;
`;

const LoadingText = styled(motion.div)`
  font-family: var(--font-mono);
  color: var(--accent-primary);
  font-size: 14px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent-primary), transparent);
  }
`;

const Loader = () => {
  const [progress, setProgress] = useState(0);
  const [console1Text, setConsole1Text] = useState('');
  const [console2Text, setConsole2Text] = useState('');
  const [console3Text, setConsole3Text] = useState('');
  const [console4Text, setConsole4Text] = useState('');
  
  const console1Content = `> Initializing breach protocol...
> Running vulnerability scan...
> Dictionary attack initiated...
> Bypassing firewall rules...
> Accessing mainframe...
> Decrypting security layers...
> Access granted!`;

  const console2Content = `$ ping -t target.sys
ICMP 32 bytes seq=1 ttl=64 time=31.2 ms
ICMP 32 bytes seq=2 ttl=64 time=28.7 ms
ICMP 32 bytes seq=3 ttl=64 time=30.1 ms
$ netstat -a
TCP 192.168.1.1:443 ESTABLISHED
TCP 10.0.0.5:21 LISTENING`;

  const console3Content = `0xF8A4 > Memory dump initiated
0xF8A8 > 01100101 01111000 01100101 01100011
0xF8B0 > Reading kernel modules...
0xF8B4 > Loading drivers...
0xF8B8 > Patching system calls...
0xF8C0 > Root access enabled`;

  const console4Content = `SSH connection established
Encryption: AES-256-GCM
Fingerprint: SHA256:dJyuw6gHt3/Rd+
Privilege escalation...
Running as root
Installing backdoor...
Connection secured`;

  // Animation for typing effect in consoles
  useEffect(() => {
    const typeText = (text, setFunction, delay, startAfter = 0) => {
  let counter = 0;
  setTimeout(() => {
    const interval = setInterval(() => {
      if (counter < text.length) {
        setFunction(text.slice(0, counter + 1));
        counter++;
      } else {
        clearInterval(interval);
      }
    }, delay);
    return () => clearInterval(interval);
  }, startAfter);
};
    
    typeText(console1Content, setConsole1Text, 30);
    typeText(console2Content, setConsole2Text, 40, 500);
    typeText(console3Content, setConsole3Text, 35, 1000);
    typeText(console4Content, setConsole4Text, 45, 1500);
    
    // Progress counter
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 1;
      });
    }, 30);
    
    return () => {
      clearInterval(progressInterval);
    };
  }, []);

  // Create matrix columns
  const matrixColumns = [];
  for (let i = 0; i < 20; i++) {
    const startPosition = Math.random() * 100;
    const speed = 0.5 + Math.random() * 1.5;
    const opacity = 0.1 + Math.random() * 0.4;
    const matrixChars = Array.from({length: 50}, () => 
      String.fromCharCode(Math.random() > 0.5 
        ? (0x30A0 + Math.random() * 100) 
        : (0x0030 + Math.random() * 10))
    ).join('');
    
    matrixColumns.push(
      <MatrixColumn 
        key={i}
        style={{ 
          left: `${startPosition}%`, 
          opacity,
          fontSize: `${12 + Math.random() * 8}px`
        }}
        animate={{ 
          y: ['0%', '120%'],
          transition: { 
            duration: speed * 20, 
            ease: 'linear', 
            repeat: Infinity,
            repeatType: 'loop'
          }
        }}
      >
        {matrixChars}
      </MatrixColumn>
    );
  }

  // Create data points for circular animation
  const dataPoints = [];
  for (let i = 0; i < 12; i++) {
    const angle = (i / 12) * Math.PI * 2;
    const x = 75 + 65 * Math.cos(angle);
    const y = 75 + 65 * Math.sin(angle);
    
    dataPoints.push(
      <DataPoint
        key={i}
        style={{ left: `${x}px`, top: `${y}px` }}
        animate={{
          opacity: [0.2, 1, 0.2],
          scale: [1, 1.5, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          delay: i * 0.2,
        }}
      />
    );
  }

  return (
    <LoaderContainer>
      <BackgroundMatrix>
        {matrixColumns}
      </BackgroundMatrix>
      
      <GridOverlay />
      
      <GlitchContainer
        animate={{ 
          x: [0, -2, 3, -1, 0],
          opacity: [1, 0.98, 1],
          transition: { 
            duration: 0.2, 
            repeat: Infinity, 
            repeatType: 'reverse',
            repeatDelay: 5
          }
        }}
      >
        <CenterContainer
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <ScanLine
            animate={{
              top: ['0%', '100%'],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'easeInOut'
            }}
          />
          
          <CircularProgress>
            <ProgressRing
              animate={{ rotate: 360 }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: 'linear' 
              }}
            />
            <InnerRing
              animate={{ rotate: -360 }}
              transition={{ 
                duration: 5, 
                repeat: Infinity, 
                ease: 'linear' 
              }}
            />
            <HexagonContainer
              animate={{ rotate: 360 }}
              transition={{ 
                duration: 10, 
                repeat: Infinity, 
                ease: 'linear' 
              }}
            >
              <Hexagon 
                animate={{ 
                  opacity: [0.3, 0.7, 0.3],
                  scale: [0.8, 1, 0.8] 
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  repeatType: 'reverse'
                }}
              />
            </HexagonContainer>
            <DataPoints>
              {dataPoints}
            </DataPoints>
            <ProgressText>
              {progress}%
            </ProgressText>
          </CircularProgress>
          
          <LoadingTexts>
            <LoadingText
              animate={{ 
                color: ['#00c8ff', '#ffffff', '#00c8ff'],
                textShadow: [
                  '0 0 5px #00c8ff', 
                  '0 0 10px #00c8ff', 
                  '0 0 5px #00c8ff'
                ]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatType: 'reverse' 
              }}
            >
              HACKING IN PROGRESS
            </LoadingText>
            <LoadingText
              animate={{ 
                opacity: [1, 0.5, 1]
              }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity,
                repeatType: 'reverse' 
              }}
            >
              SYSTEM BREACH {progress > 75 ? 'IMMINENT' : 'INITIATED'}
            </LoadingText>
          </LoadingTexts>
          
          <MultipleConsoles>
            <ConsoleWindow
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ConsoleHeader>
                <span>Breach Protocol</span>
                <span>ICE-breaker.exe</span>
              </ConsoleHeader>
              <ConsoleText>
                <pre>{console1Text}</pre>
              </ConsoleText>
            </ConsoleWindow>
            
            <ConsoleWindow
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <ConsoleHeader>
                <span>Network Scan</span>
                <span>nmap.exe</span>
              </ConsoleHeader>
              <ConsoleText>
                <pre>{console2Text}</pre>
              </ConsoleText>
            </ConsoleWindow>
            
            <ConsoleWindow
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <ConsoleHeader>
                <span>Memory Analysis</span>
                <span>kernel_mod.sys</span>
              </ConsoleHeader>
              <ConsoleText>
                <pre>{console3Text}</pre>
              </ConsoleText>
            </ConsoleWindow>
            
            <ConsoleWindow
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <ConsoleHeader>
                <span>Remote Access</span>
                <span>backdoor.sh</span>
              </ConsoleHeader>
              <ConsoleText>
                <pre>{console4Text}</pre>
              </ConsoleText>
            </ConsoleWindow>
          </MultipleConsoles>
        </CenterContainer>
      </GlitchContainer>
    </LoaderContainer>
  );
};

export default Loader; 